import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin, Tag } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import BotConversations from "./BotConversations";
import BotWizard from "./BotWizard";
import BotHandoffSettings from "./BotHandoffSettings";
import BotHandoffLiveConsole from "./BotHandoffLiveConsole";
import IntentInsightsTab from "./intentInsights/IntentInsightsTab";
import CalendarTab from "../SonicCalendar/CalendarTab";
import { getProspectBot } from "./prospectService";
import { getBotStatus, nameNeedsRename, statusTagColor } from "./channelKind";
import { ChannelIconRow } from "./ChannelsSection";
import styles from "./style.module.scss";

const TABS = [
  { id: "conversations", label: "sonic_prospect.tab_conversations" },
  { id: "settings", label: "sonic_prospect.tab_settings" },
  { id: "insights", label: "sonic_prospect.tab_insights" },
  { id: "calendar", label: "sonic_prospect.tab_calendar" },
  { id: "handoff", label: "sonic_prospect.tab_handoff" },
  { id: "live", label: "sonic_prospect.tab_live" },
];

const BotDetail = ({
  bot,
  tab,
  onTab,
  onBack,
  onSaved,
  onDeleted,
  prospectLimits,
  userId,
  whatsappConfig,
}) => {
  const { t } = useTranslation();
  const [full, setFull] = useState(bot);
  const [loading, setLoading] = useState(false);
  const [renameTick, setRenameTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!bot?.id) return undefined;
    setLoading(true);
    getProspectBot(bot.id)
      .then((next) => {
        if (!cancelled && next) setFull(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bot]);

  const current = full || bot;
  const status = getBotStatus(current);
  const botName = current?.name || t("sonic_prospect.preview_bot_fallback");
  const nameClash = nameNeedsRename(current);

  useEffect(() => {
    const settingsOpen = tab === "settings" || tab === "embed";
    if (settingsOpen) document.documentElement.dataset.spSettings = "1";
    else delete document.documentElement.dataset.spSettings;
    return () => {
      delete document.documentElement.dataset.spSettings;
    };
  }, [tab]);

  return (
    <div className={styles.detailPage}>
      <p className={styles.breadcrumb}>
        <button type="button" onClick={onBack}>
          {t("sonic_prospect.breadcrumb_root")}
        </button>
        <span aria-hidden="true"> / </span>
        <span>{botName}</span>
      </p>
      <header className={styles.detailHead}>
        <div>
          <div className={styles.detailTitleRow}>
            <GlobalOutlined className={styles.detailIcon} aria-hidden="true" />
            <h1>{botName}</h1>
            <Tag color={statusTagColor(status)}>
              {t(`sonic_prospect.channel_status_${status}`)}
            </Tag>
          </div>
          <ChannelIconRow bot={current} t={t} />
          {nameClash ? (
            <p className={styles.renameHint}>
              {t("sonic_prospect.rename_hint")}{" "}
              <button
                type="button"
                className={styles.textLink}
                onClick={() => {
                  setRenameTick((n) => n + 1);
                  onTab("settings");
                }}
              >
                {t("sonic_prospect.rename_cta")}
              </button>
            </p>
          ) : null}
        </div>
      </header>
      <div className={styles.detailTabs} role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={
              tab === item.id || (item.id === "settings" && tab === "embed")
                ? styles.detailTabOn
                : styles.detailTab
            }
            onClick={() => onTab(item.id)}
          >
            {t(item.label)}
          </button>
        ))}
      </div>
      {loading && !full ? (
        <div className={styles.centered}>
          <Spin />
        </div>
      ) : null}
      {tab === "conversations" ? (
        <BotConversations
          bot={current}
          onBack={onBack}
          prospectLimits={prospectLimits}
          embedded
          setupPending={status !== "live"}
          onGetEmbed={() => onTab("embed")}
        />
      ) : null}
      {tab === "settings" || tab === "embed" ? (
        <BotWizard
          bot={current}
          onBack={onBack}
          onSaved={onSaved}
          onDeleted={onDeleted}
          prospectLimits={prospectLimits}
          whatsappConfig={whatsappConfig}
          embedded
          initialStep={tab === "embed" ? 3 : 0}
          focusName={renameTick}
        />
      ) : null}
      {tab === "insights" ? (
        <IntentInsightsTab
          botId={current?.id}
          botName={current?.name}
          onViewAllChats={() => onTab("conversations")}
        />
      ) : null}
      {tab === "calendar" ? <CalendarTab bot={current} /> : null}
      {tab === "handoff" ? (
        <BotHandoffSettings
          bot={current}
          onBack={onBack}
          onSaved={onSaved}
          onOpenLive={() => onTab("live")}
          embedded
        />
      ) : null}
      {tab === "live" ? (
        <BotHandoffLiveConsole
          bot={current}
          onBack={onBack}
          userId={userId}
          embedded
        />
      ) : null}
    </div>
  );
};

export default BotDetail;
